import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddBusinessRoundedIcon from '@mui/icons-material/AddBusinessRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const STORAGE_KEY = 'brutos-platform-admin-session';
const ADMIN_HOSTS = new Set([
  'admingestor.brutosdelivery.cloud',
  'admin.brutosdelivery.cloud',
]);
const PRIMARY_ADMIN_HOST = 'admingestor.brutosdelivery.cloud';

const resolveApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window === 'undefined') return '';
  const { hostname, protocol, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (port === '3002') return `${protocol}//${hostname}:3001`;
    return `${protocol}//${hostname}:3001`;
  }
  if (ADMIN_HOSTS.has(hostname)) {
    return `${protocol}//backend.brutosdelivery.cloud`;
  }
  return '';
};

const API_URL = resolveApiUrl();
const LOCAL_TENANT_CREATION_ALLOWED = (
  process.env.REACT_APP_ALLOW_LOCAL_TENANT_CREATION === 'true' ||
  process.env.NODE_ENV !== 'production'
);

const emptyForm = {
  cnpj: '',
  legalName: '',
  cep: '',
  street: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  email: '',
  temporaryPassword: '',
};

const getStoredSession = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed?.token || !parsed?.user) return null;
    return parsed;
  } catch {
    return null;
  }
};

const setStoredSession = (session) => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
};

const clearStoredSession = () => {
  window.localStorage.removeItem(STORAGE_KEY);
};

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const formatCnpj = (value) => {
  const digits = onlyDigits(value).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const normalizeCompanyToForm = (company = {}) => ({
  cnpj: company.cnpj || '',
  legalName: company.legalName || '',
  cep: company.address?.cep || '',
  street: company.address?.street || '',
  number: company.address?.number || '',
  neighborhood: company.address?.neighborhood || '',
  city: company.address?.city || '',
  state: company.address?.state || '',
  email: company.email || company.owner?.email || '',
  temporaryPassword: '',
});

const buildPayload = (form) => ({
  cnpj: onlyDigits(form.cnpj),
  legalName: form.legalName.trim(),
  email: form.email.trim(),
  temporaryPassword: form.temporaryPassword,
  address: {
    cep: form.cep.trim(),
    street: form.street.trim(),
    number: form.number.trim(),
    neighborhood: form.neighborhood.trim(),
    city: form.city.trim(),
    state: form.state.trim().toUpperCase(),
  },
});

const LoginView = ({ onLogin }) => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/platform-admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao entrar.');
      onLogin({ token: data.token, user: data.user });
    } catch (err) {
      setError(err.message || 'Falha ao entrar.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', px: 2, background: '#111827' }}>
      <Paper sx={{ width: '100%', maxWidth: 460, p: 4, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Avatar sx={{ bgcolor: '#f97316', width: 56, height: 56 }}>
              <ShieldRoundedIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Brutos Delivery Admin
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Acesso administrativo de empresas e logins principais.
            </Typography>
          </Stack>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {error ? <Alert severity="error">{error}</Alert> : null}
              <TextField label="E-mail" value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="username" required fullWidth />
              <TextField
                label="Senha"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                        {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" size="large" variant="contained" disabled={submitting}>
                {submitting ? 'Entrando...' : 'Entrar'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

function App() {
  const [session, setSession] = useState(() => getStoredSession());
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(Boolean(session?.token));
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, mode: 'create', company: null });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [showTemporaryPassword, setShowTemporaryPassword] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const activeCompanies = useMemo(() => companies.filter((company) => company.status === 'active'), [companies]);

  const apiFetch = useCallback(async (path, options = {}) => {
    const headers = new Headers(options.headers || {});
    if (session?.token) headers.set('Authorization', `Bearer ${session.token}`);
    const response = await fetch(`${API_URL}${path}`, { ...options, headers });
    if (response.status === 401) {
      clearStoredSession();
      setSession(null);
    }
    return response;
  }, [session?.token]);

  const loadCompanies = useCallback(async () => {
    if (!session?.token) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/api/platform-admin/companies');
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao carregar empresas.');
      setCompanies(Array.isArray(data.companies) ? data.companies : []);
    } catch (err) {
      setError(err.message || 'Falha ao carregar empresas.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, session?.token]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleLogin = (nextSession) => {
    setStoredSession(nextSession);
    setSession(nextSession);
  };

  const logout = () => {
    clearStoredSession();
    setSession(null);
  };

  const openDialog = (mode, company = null) => {
    setDialog({ open: true, mode, company });
    setForm(company ? normalizeCompanyToForm(company) : emptyForm);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialog({ open: false, mode: 'create', company: null });
    setForm(emptyForm);
  };

  const updateForm = (field) => (event) => {
    const value = field === 'cnpj' ? formatCnpj(event.target.value) : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (dialog.mode === 'create' && !LOCAL_TENANT_CREATION_ALLOWED) {
      setToast({
        open: true,
        message: 'Criação oficial de tenants deve ser feita pelo Brutos Admin Central.',
        severity: 'warning',
      });
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (dialog.mode === 'edit' && !payload.temporaryPassword) {
        delete payload.temporaryPassword;
      }
      const response = await apiFetch(
        dialog.mode === 'edit' ? `/api/platform-admin/companies/${dialog.company.id}` : '/api/platform-admin/companies',
        {
          method: dialog.mode === 'edit' ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao salvar.');
      setToast({ open: true, message: dialog.mode === 'edit' ? 'Empresa atualizada.' : 'Empresa criada.', severity: 'success' });
      closeDialog();
      await loadCompanies();
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao salvar.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Excluir o acesso de ${company.legalName}?`)) return;
    setDeletingId(company.id);
    try {
      const response = await apiFetch(`/api/platform-admin/companies/${company.id}`, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao excluir.');
      setToast({ open: true, message: 'Acesso removido.', severity: 'success' });
      await loadCompanies();
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao excluir.', severity: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (company) => {
    const isActive = company.status === 'active';
    const nextStatus = isActive ? 'inactive' : 'active';
    const action = isActive ? 'desativar' : 'reativar';
    const impact = isActive
      ? 'Os usuários deste tenant deixarão de acessar o painel operacional.'
      : 'Os usuários deste tenant voltarão a acessar o painel operacional.';

    if (!window.confirm(`Deseja ${action} ${company.legalName}? ${impact}`)) return;
    setStatusUpdatingId(company.id);
    try {
      const response = await apiFetch(`/api/platform-admin/companies/${company.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Falha ao atualizar status.');
      setToast({
        open: true,
        message: isActive ? 'Tenant desativado.' : 'Tenant reativado.',
        severity: 'success',
      });
      await loadCompanies();
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao atualizar status.', severity: 'error' });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  if (!session?.token) {
    return <LoginView onLogin={handleLogin} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fb' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#111827', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Toolbar>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexGrow: 1 }}>
            <Avatar sx={{ bgcolor: '#f97316' }}>
              <BusinessRoundedIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                Brutos Delivery Admin
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)' }}>
                {session.user?.email}
              </Typography>
            </Box>
          </Stack>
          <Tooltip title="Atualizar">
            <IconButton color="inherit" onClick={loadCompanies}>
              <RefreshRoundedIcon />
            </IconButton>
          </Tooltip>
          <Button color="inherit" startIcon={<LogoutRoundedIcon />} onClick={logout}>
            Sair
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900 }}>
                Usuários do Brutos Delivery
              </Typography>
              <Typography color="text.secondary">
                {activeCompanies.length} empresas ativas com dados isolados por login.
              </Typography>
            </Box>
            {LOCAL_TENANT_CREATION_ALLOWED ? (
              <Button startIcon={<AddBusinessRoundedIcon />} size="large" variant="contained" onClick={() => openDialog('create')}>
                Novo usuário
              </Button>
            ) : null}
          </Stack>

          <Alert severity="info">
            Criação oficial de tenants deve ser feita pelo Brutos Admin Central.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="overline" color="text.secondary">Ativos</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900 }}>{activeCompanies.length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="overline" color="text.secondary">Cadastrados</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900 }}>{companies.length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="overline" color="text.secondary">Domínio</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{PRIMARY_ADMIN_HOST}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading ? (
            <Paper sx={{ p: 4, borderRadius: 3, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Empresa</TableCell>
                    <TableCell>CNPJ</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Cidade</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 800 }}>{company.legalName}</Typography>
                          <Typography variant="caption" color="text.secondary">{company.owner?.login || 'Sem usuario principal'}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{formatCnpj(company.cnpj)}</TableCell>
                      <TableCell>{company.email}</TableCell>
                      <TableCell>{[company.address?.city, company.address?.state].filter(Boolean).join(' - ')}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Chip size="small" color={company.status === 'active' ? 'success' : 'default'} label={company.status === 'active' ? 'Ativo' : 'Inativo'} />
                          {company.isDefault ? <Chip size="small" variant="outlined" label="Padrão" /> : null}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton onClick={() => openDialog('edit', company)}>
                            <EditRoundedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={company.status === 'active' ? 'Desativar tenant' : 'Ativar tenant'}>
                          <span>
                            <IconButton
                              color={company.status === 'active' ? 'warning' : 'success'}
                              disabled={company.isDefault || statusUpdatingId === company.id}
                              onClick={() => handleToggleStatus(company)}
                            >
                              {company.status === 'active' ? <BlockRoundedIcon /> : <CheckCircleRoundedIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <span>
                            <IconButton color="error" disabled={company.isDefault || deletingId === company.id || statusUpdatingId === company.id} onClick={() => handleDelete(company)}>
                              <DeleteOutlineRoundedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Container>

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.mode === 'edit' ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField label="CNPJ" value={form.cnpj} onChange={updateForm('cnpj')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField label="Razão Social" value={form.legalName} onChange={updateForm('legalName')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="CEP" value={form.cep} onChange={updateForm('cep')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField label="Rua" value={form.street} onChange={updateForm('street')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Número" value={form.number} onChange={updateForm('number')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Bairro" value={form.neighborhood} onChange={updateForm('neighborhood')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Cidade" value={form.city} onChange={updateForm('city')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField label="Estado" value={form.state} onChange={updateForm('state')} required fullWidth inputProps={{ maxLength: 2 }} />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="E-mail" value={form.email} onChange={updateForm('email')} required fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={dialog.mode === 'edit' ? 'Nova senha temporária' : 'Senha temporária'}
                  value={form.temporaryPassword}
                  onChange={updateForm('temporaryPassword')}
                  type={showTemporaryPassword ? 'text' : 'password'}
                  required={dialog.mode === 'create'}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowTemporaryPassword((prev) => !prev)} edge="end">
                          {showTemporaryPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3200} onClose={() => setToast((prev) => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default App;
